import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';
import {
  Building2, Users, Calendar, MapPin, ArrowUpDown, ArrowUp, ArrowDown,
  Search as SearchIcon, Sparkles, Info,
} from 'lucide-react';
import { getMapLocations } from '../api';
import { getApiErrorMessage } from '../utils/apiError';
import { relativeDays, compactNumber } from '../utils/format';
import CITY_REFERENCE, { cityInfo, elderlyCount, UK_REGIONS, REGION_COLORS } from '../data/cityReference';
import StatTile from '../components/ui/StatTile';
import SectionHeader from '../components/ui/SectionHeader';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';

// Column definitions. `w` sets a min width so the table scrolls instead of
// squishing on narrow screens.
const COLUMNS = [
  { key: 'city', label: 'City', align: 'left', w: 'min-w-[150px]', sticky: true },
  { key: 'region', label: 'Region', align: 'left', w: 'min-w-[150px]' },
  { key: 'district', label: 'District', align: 'left', w: 'min-w-[170px]' },
  { key: 'population', label: 'Population', align: 'right', w: 'min-w-[100px]' },
  { key: 'elderlyPercent', label: '65+ %', align: 'right', w: 'min-w-[72px]' },
  { key: 'dentists', label: 'Dentists~', align: 'right', w: 'min-w-[90px]' },
  { key: 'elderlyPerDentist', label: '65+ / dentist', align: 'right', w: 'min-w-[104px]' },
  { key: 'eventCount', label: 'Events', align: 'right', w: 'min-w-[80px]' },
  { key: 'clinicCount', label: 'Clinics', align: 'right', w: 'min-w-[80px]' },
  { key: 'opportunityScore', label: 'Opportunity', align: 'right', w: 'min-w-[120px]' },
  { key: 'daysSinceLast', label: 'Last activity', align: 'right', w: 'min-w-[110px]' },
];

// --- Opportunity algorithm ---------------------------------------------------
// Surfaces under-served, under-competed markets. For each city with reference
// data we blend DEMAND (ageing residents per dentist, market size, ageing
// share) against COMPETITION (competitor events + clinics already there).
// score = 100 * (0.65*demand + 0.35*(1 - competition)), each metric min-max
// normalised across the dataset.
function withOpportunity(rows) {
  const minmax = (arr) => {
    const v = arr.filter((x) => x != null);
    return v.length ? [Math.min(...v), Math.max(...v)] : [0, 1];
  };
  const norm = (val, [mn, mx]) => (val == null ? null : mx === mn ? 0.5 : (val - mn) / (mx - mn));

  const withData = rows.filter((r) => r.population != null && r.dentists && r.elderlyPerDentist != null);
  const epdR = minmax(withData.map((r) => r.elderlyPerDentist));
  const popR = minmax(withData.map((r) => r.population));
  const eldR = minmax(withData.map((r) => r.elderlyPercent));
  const evR = minmax(rows.map((r) => r.eventCount));
  const clR = minmax(rows.map((r) => r.clinicCount));

  return rows.map((r) => {
    if (r.population == null || !r.dentists || r.elderlyPerDentist == null) {
      return { ...r, opportunityScore: null, opportunityReasons: [], demandPct: null, competitionPct: null };
    }
    const nEpd = norm(r.elderlyPerDentist, epdR);
    const nPop = norm(r.population, popR);
    const nEld = norm(r.elderlyPercent, eldR);
    const demand = 0.5 * nEpd + 0.3 * nPop + 0.2 * nEld;
    const competition = 0.6 * norm(r.eventCount, evR) + 0.4 * norm(r.clinicCount, clR);
    const score = Math.round(100 * (0.65 * demand + 0.35 * (1 - competition)));

    const reasons = [];
    if (nEpd > 0.66) reasons.push('Underserved 65+');
    if (nPop > 0.66) reasons.push('Large market');
    if (nEld > 0.66) reasons.push('Ageing population');
    if (competition < 0.34) reasons.push('Low competition');
    if (r.eventCount === 0) reasons.push('Untapped');
    return {
      ...r,
      opportunityScore: score,
      opportunityReasons: reasons,
      demandPct: Math.round(demand * 100),
      competitionPct: Math.round(competition * 100),
    };
  });
}

// Weights surfaced in the UI so the score is fully transparent.
const SCORING = {
  demand: [
    { label: '65+ residents per dentist (under-served)', weight: 50 },
    { label: 'Market size (population)', weight: 30 },
    { label: 'Ageing share (65+ %)', weight: 20 },
  ],
  competition: [
    { label: 'Competitor events here', weight: 60 },
    { label: 'Competing clinics here', weight: 40 },
  ],
  final: 'Opportunity = 65% × Demand + 35% × (100 − Competition)',
};

function scoreTone(score) {
  if (score == null) return 'bg-ink-100 text-ink-400';
  if (score >= 70) return 'bg-green-100 text-green-700';
  if (score >= 50) return 'bg-amber-100 text-amber-700';
  return 'bg-ink-100 text-ink-500';
}

function Cities() {
  const navigate = useNavigate();
  const [merged, setMerged] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [region, setRegion] = useState('');
  const [search, setSearch] = useState('');
  const [eventFilter, setEventFilter] = useState('all'); // all | with | without
  const [sort, setSort] = useState({ key: 'opportunityScore', dir: 'desc' });
  const [showScoring, setShowScoring] = useState(false);

  useEffect(() => {
    document.title = 'Cities | Dental Events';
    return () => { document.title = 'Dental Events'; };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getMapLocations()
      .then((res) => { if (active) { buildRows(res.data.data); setError(null); } })
      .catch((err) => { if (active) setError(getApiErrorMessage(err, 'Failed to load city data')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  function buildRows(eventLocations) {
    const map = new Map();
    for (const [key, info] of Object.entries(CITY_REFERENCE)) {
      const display = key.replace(/\b\w/g, (c) => c.toUpperCase());
      map.set(key, {
        key, city: display, region: info.region, district: info.district,
        population: info.population ?? null, elderlyPercent: info.elderlyPercent ?? null,
        dentists: info.dentists ?? null, eventCount: 0, clinicCount: 0, lastActive: null, daysSinceLast: null,
      });
    }
    for (const loc of eventLocations) {
      const info = cityInfo(loc.location);
      const key = (loc.location || '').toLowerCase();
      const existing = [...map.values()].find(
        (r) => r.key === key || (info && r.district === info.district && r.region === info.region)
      );
      if (existing) {
        existing.eventCount += loc.eventCount;
        existing.clinicCount = Math.max(existing.clinicCount, loc.clinicCount);
        existing.lastActive = loc.lastActive || existing.lastActive;
        existing.daysSinceLast = loc.daysSinceLast ?? existing.daysSinceLast;
        existing.eventLocation = loc.location;
      } else {
        map.set(key, {
          key, city: loc.location, region: info?.region || '—', district: info?.district || '—',
          population: info?.population ?? null, elderlyPercent: info?.elderlyPercent ?? null,
          dentists: info?.dentists ?? null, eventCount: loc.eventCount, clinicCount: loc.clinicCount,
          lastActive: loc.lastActive, daysSinceLast: loc.daysSinceLast, eventLocation: loc.location,
        });
      }
    }
    const out = [...map.values()].map((r) => ({
      ...r,
      elderlyPerDentist: r.dentists && elderlyCount(r) ? Math.round(elderlyCount(r) / r.dentists) : null,
    }));
    setMerged(withOpportunity(out));
  }

  const filtered = useMemo(() => {
    let out = merged;
    if (region) out = out.filter((r) => r.region === region);
    if (eventFilter === 'with') out = out.filter((r) => r.eventCount > 0);
    if (eventFilter === 'without') out = out.filter((r) => r.eventCount === 0);
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((r) => r.city.toLowerCase().includes(q) || (r.district || '').toLowerCase().includes(q));
    }
    const { key, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    return [...out].sort((a, b) => {
      const av = a[key]; const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mul;
      return String(av).localeCompare(String(bv)) * mul;
    });
  }, [merged, region, eventFilter, search, sort]);

  const regionAgg = useMemo(() => {
    const m = {};
    for (const r of merged) {
      if (!r.region || r.region === '—') continue;
      m[r.region] = (m[r.region] || 0) + r.eventCount;
    }
    return UK_REGIONS.filter((rg) => m[rg] != null).map((rg) => ({ region: rg, events: m[rg] || 0 }))
      .sort((a, b) => b.events - a.events);
  }, [merged]);

  const topOpportunities = useMemo(
    () => [...merged]
      .filter((r) => r.opportunityScore != null && (!region || r.region === region))
      .sort((a, b) => b.opportunityScore - a.opportunityScore)
      .slice(0, 6),
    [merged, region]
  );

  const totalEvents = merged.reduce((s, r) => s + r.eventCount, 0);
  const citiesWithEvents = merged.filter((r) => r.eventCount > 0).length;

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));

  const SortIcon = ({ col }) => {
    if (sort.key !== col) return <ArrowUpDown className="w-3 h-3 inline opacity-40" />;
    return sort.dir === 'asc' ? <ArrowUp className="w-3 h-3 inline" /> : <ArrowDown className="w-3 h-3 inline" />;
  };

  const fmt = (r, key) => {
    const v = r[key];
    if (v == null) return <span className="text-ink-300">—</span>;
    if (key === 'population') return compactNumber(v);
    if (key === 'elderlyPercent') return `${v}%`;
    if (key === 'daysSinceLast') return relativeDays(v);
    if (key === 'dentists' || key === 'elderlyPerDentist') return v.toLocaleString();
    return v;
  };

  const EVENT_TABS = [
    { key: 'all', label: 'All' },
    { key: 'with', label: 'With events' },
    { key: 'without', label: 'Without events' },
  ];

  return (
    <div className="space-y-5 sm:space-y-6 animate-fade-in-up">
      <div>
        <h1 className="page-title mb-1">Cities</h1>
        <p className="text-sm text-ink-500">
          UK locations by region & district, matched with population, ageing (65+), dentist supply and competitor activity.
          <span className="text-ink-400"> Demographics are approximate static reference data.</span>
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatTile icon={MapPin} label="Cities tracked" value={merged.length} tone="dental" />
        <StatTile icon={Calendar} label="Total events" value={totalEvents} tone="accent" />
        <StatTile icon={Building2} label="Cities with events" value={citiesWithEvents} tone="amber" />
        <StatTile icon={Users} label="Regions" value={regionAgg.length} tone="blue" />
      </div>

      {/* Opportunities */}
      {topOpportunities.length > 0 && (
        <div>
          <SectionHeader
            icon={Sparkles}
            title="Top opportunities"
            subtitle="Under-served, under-competed markets (ageing demand vs dentist supply vs competitor activity)"
            action={
              <button onClick={() => setShowScoring((v) => !v)} className="text-xs font-medium text-dental-600 hover:text-dental-700 inline-flex items-center gap-1">
                <Info className="w-3.5 h-3.5" /> {showScoring ? 'Hide' : 'How is this scored?'}
              </button>
            }
          />

          {showScoring && (
            <div className="card mb-4 bg-ink-50/60">
              <p className="text-sm font-semibold text-ink-800 mb-2">Scoring breakdown</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs font-semibold text-green-700 mb-1">Demand (the pull)</p>
                  <ul className="space-y-1">
                    {SCORING.demand.map((d) => (
                      <li key={d.label} className="flex justify-between gap-3 text-ink-600">
                        <span>{d.label}</span><span className="font-semibold tabular-nums">{d.weight}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-semibold text-red-600 mb-1">Competition (the push-back)</p>
                  <ul className="space-y-1">
                    {SCORING.competition.map((d) => (
                      <li key={d.label} className="flex justify-between gap-3 text-ink-600">
                        <span>{d.label}</span><span className="font-semibold tabular-nums">{d.weight}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <p className="mt-3 text-sm text-ink-700 font-medium bg-white rounded-lg px-3 py-2 ring-1 ring-ink-200">
                {SCORING.final}
              </p>
              <p className="mt-2 text-xs text-ink-400">All inputs are min-max normalised (0–100) across the tracked cities, so scores are relative within this dataset.</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {topOpportunities.map((r) => (
              <button
                key={r.key}
                onClick={() => navigate(`/locations/${encodeURIComponent(r.eventLocation || r.city)}`)}
                className="card card-hover text-left"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-ink-900 truncate">{r.city}</p>
                    <p className="text-xs text-ink-500 truncate">{r.region}</p>
                  </div>
                  <div className={`shrink-0 px-2 py-1 rounded-lg text-sm font-bold ${scoreTone(r.opportunityScore)}`}>
                    {r.opportunityScore}
                  </div>
                </div>

                {/* Demand vs competition breakdown */}
                <div className="mt-3 space-y-1.5">
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="w-20 text-ink-500 shrink-0">Demand</span>
                    <div className="flex-1 h-1.5 rounded-full bg-ink-100 overflow-hidden">
                      <div className="h-full rounded-full bg-green-500" style={{ width: `${r.demandPct}%` }} />
                    </div>
                    <span className="w-6 text-right tabular-nums text-ink-600">{r.demandPct}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="w-20 text-ink-500 shrink-0">Competition</span>
                    <div className="flex-1 h-1.5 rounded-full bg-ink-100 overflow-hidden">
                      <div className="h-full rounded-full bg-red-400" style={{ width: `${r.competitionPct}%` }} />
                    </div>
                    <span className="w-6 text-right tabular-nums text-ink-600">{r.competitionPct}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1 mt-2.5">
                  {r.opportunityReasons.map((reason) => (
                    <span key={reason} className="chip py-0.5 text-[11px]">{reason}</span>
                  ))}
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                  <div><p className="text-ink-400">65+/dentist</p><p className="font-semibold text-ink-800">{r.elderlyPerDentist?.toLocaleString() ?? '—'}</p></div>
                  <div><p className="text-ink-400">Events</p><p className="font-semibold text-ink-800">{r.eventCount}</p></div>
                  <div><p className="text-ink-400">Clinics</p><p className="font-semibold text-ink-800">{r.clinicCount}</p></div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Events by region */}
      {regionAgg.length > 0 && (
        <div className="card">
          <SectionHeader icon={Calendar} title="Events by region" subtitle="Click a bar to filter the table" />
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={regionAgg} margin={{ top: 6, right: 6, left: -16, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="region" tick={{ fontSize: 10, fill: '#64748b' }} angle={-30} textAnchor="end" interval={0} height={60} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: '#f1f5f9' }} contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }} />
                <Bar dataKey="events" radius={[6, 6, 0, 0]} cursor="pointer" onClick={(d) => d?.region && setRegion(region === d.region ? '' : d.region)}>
                  {regionAgg.map((r) => (
                    <Cell key={r.region} fill={REGION_COLORS[r.region] || '#0d9488'} fillOpacity={!region || region === r.region ? 1 : 0.3} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="card flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="relative flex-1">
          <SearchIcon className="w-4 h-4 text-ink-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input className="input pl-9" placeholder="Search city or district…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="input lg:w-52" value={region} onChange={(e) => setRegion(e.target.value)}>
          <option value="">All regions</option>
          {UK_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <div className="flex rounded-xl ring-1 ring-ink-200 overflow-hidden shrink-0">
          {EVENT_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setEventFilter(t.key)}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                eventFilter === t.key ? 'bg-dental-600 text-white' : 'bg-white text-ink-600 hover:bg-ink-100'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="card bg-red-50 ring-red-200"><p className="text-red-700">{error}</p></div>}

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={MapPin} title="No cities match" message="Try clearing filters." />
      ) : (
        <div className="card p-0">
          <div className="overflow-x-auto rounded-2xl">
            <table className="w-full min-w-[1080px] text-sm border-collapse">
              <thead>
                <tr className="border-b border-ink-200 bg-ink-50">
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      onClick={() => toggleSort(c.key)}
                      className={`px-3 py-2.5 font-semibold text-ink-600 whitespace-nowrap cursor-pointer select-none hover:text-ink-900 ${c.w} ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.sticky ? 'sticky left-0 z-10 bg-ink-50' : ''}`}
                    >
                      {c.label} <SortIcon col={c.key} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.key}
                    onClick={() => navigate(`/locations/${encodeURIComponent(r.eventLocation || r.city)}`)}
                    className="border-b border-ink-100 last:border-0 hover:bg-dental-50/40 cursor-pointer transition-colors group"
                  >
                    <td className="px-3 py-2.5 font-medium text-ink-900 whitespace-nowrap sticky left-0 z-10 bg-white group-hover:bg-dental-50">
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: REGION_COLORS[r.region] || '#cbd5e1' }} />
                        {r.city}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-ink-500 whitespace-nowrap">{r.region}</td>
                    <td className="px-3 py-2.5 text-ink-500 whitespace-nowrap">{r.district}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{fmt(r, 'population')}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{fmt(r, 'elderlyPercent')}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{fmt(r, 'dentists')}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{fmt(r, 'elderlyPerDentist')}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-dental-700">{r.eventCount || <span className="text-ink-300">0</span>}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{r.clinicCount || <span className="text-ink-300">0</span>}</td>
                    <td className="px-3 py-2.5 text-right">
                      {r.opportunityScore == null ? <span className="text-ink-300">—</span> : (
                        <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-bold tabular-nums ${scoreTone(r.opportunityScore)}`}>{r.opportunityScore}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap text-ink-500">{fmt(r, 'daysSinceLast')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-ink-400">
        Dentists~ and demographics are approximate reference figures (Census-2021 era), editable in
        <code className="mx-1 px-1 rounded bg-ink-100">src/data/cityReference.js</code>.
        Opportunity = ageing demand (65+/dentist, market size, ageing share) weighed against competitor activity (events + clinics); higher = more under-served.
      </p>
    </div>
  );
}

export default Cities;
